import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  DashboardResponse,
  EventReportDetailResponse,
  EventReportListResponse,
  Locale,
  SalesReportQuery,
  SalesReportResponse,
} from "@ticketnjoy/types";

import { appendRangeParams, type ReportRangeQuery } from "@/features/reports/range";
import {
  API_BASE_URL,
  ApiError,
  apiClient,
  problemToApiError,
} from "@/lib/api-client";

export const dashboardQueryKey = (
  range: ReportRangeQuery,
  shopId: string | null,
) => ["reports", "dashboard", range.range, range.from ?? "", range.to ?? "", shopId ?? "all"] as const;

/**
 * GET /reports/dashboard?range=...&from=&to=&shopId=...
 *
 * Cache: 30s — KPI verisi yüksek-volatil değil; sayfa odağa geri dönünce
 * yeniden çekilir (refetchOnWindowFocus default'u). Range/shop değişimi anında
 * yeni query key oluşturur.
 */
export function useDashboardQuery(
  range: ReportRangeQuery,
  shopId: string | null,
  enabled = true,
) {
  return useQuery<DashboardResponse, ApiError>({
    queryKey: dashboardQueryKey(range, shopId),
    enabled,
    queryFn: () => {
      const params = appendRangeParams(new URLSearchParams(), range);
      if (shopId) params.set("shopId", shopId);
      return apiClient<DashboardResponse>(
        `/reports/dashboard?${params.toString()}`,
      );
    },
    staleTime: 30 * 1000,
  });
}

// ---------- Sales report (P4) ----------

export const salesQueryKey = (q: SalesReportQuery) =>
  [
    "reports",
    "sales",
    q.range,
    q.from ?? "",
    q.to ?? "",
    q.shopId ?? "all",
    q.eventId ?? "all",
    q.ticketTypeId ?? "all",
    q.groupBy,
  ] as const;

/**
 * Sales raporu query parametrelerini URLSearchParams'a çevirir. Export
 * butonları da aynı string'i kullanır → JSON ve dosya endpoint'i identik
 * filtre garantisi.
 */
export function buildSalesParams(q: SalesReportQuery): URLSearchParams {
  const params = appendRangeParams(new URLSearchParams(), {
    range: q.range,
    from: q.from ?? null,
    to: q.to ?? null,
  });
  params.set("groupBy", q.groupBy);
  if (q.shopId) params.set("shopId", q.shopId);
  if (q.eventId) params.set("eventId", q.eventId);
  if (q.ticketTypeId) params.set("ticketTypeId", q.ticketTypeId);
  return params;
}

export function useSalesQuery(q: SalesReportQuery, enabled: boolean = true) {
  return useQuery<SalesReportResponse, ApiError>({
    queryKey: salesQueryKey(q),
    queryFn: () =>
      apiClient<SalesReportResponse>(
        `/reports/sales?${buildSalesParams(q).toString()}`,
      ),
    enabled,
    staleTime: 60 * 1000,
  });
}

// ---------- Event report snapshots (P5) ----------

export interface EventReportListParams {
  from?: string | null;
  to?: string | null;
  shopId?: string | null;
  page?: number;
  pageSize?: number;
}

export const eventReportsListKey = (params: EventReportListParams) =>
  [
    "reports",
    "events",
    "list",
    params.from ?? "",
    params.to ?? "",
    params.shopId ?? "all",
    params.page ?? 1,
    params.pageSize ?? 25,
  ] as const;

export const eventReportDetailKey = (eventId: string) =>
  ["reports", "events", "detail", eventId] as const;

/**
 * GET /reports/events?from=&to=&shopId=&page=&pageSize=
 *
 * Snapshot tablosu zaten önceden hesaplanmış, sayfa kararlı; agresif cache (60s).
 */
export function useEventReportsListQuery(params: EventReportListParams) {
  return useQuery<EventReportListResponse, ApiError>({
    queryKey: eventReportsListKey(params),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (params.from) sp.set("from", params.from);
      if (params.to) sp.set("to", params.to);
      if (params.shopId) sp.set("shopId", params.shopId);
      if (params.page) sp.set("page", String(params.page));
      if (params.pageSize) sp.set("pageSize", String(params.pageSize));
      const qs = sp.toString();
      return apiClient<EventReportListResponse>(
        qs ? `/reports/events?${qs}` : `/reports/events`,
      );
    },
    staleTime: 60 * 1000,
  });
}

/**
 * GET /reports/events/{eventId}
 *
 * Detay payload immutable; uzun cache (5dk). Manual regenerate sonrası
 * mutation invalidate eder.
 */
export function useEventReportDetailQuery(eventId: string | null) {
  return useQuery<EventReportDetailResponse, ApiError>({
    queryKey: eventReportDetailKey(eventId ?? ""),
    queryFn: () =>
      apiClient<EventReportDetailResponse>(`/reports/events/${eventId}`),
    enabled: !!eventId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * POST /reports/events/{eventId}/regenerate
 *
 * Owner-only. Eski snapshot silinir, yeniden üretilir. Audit log'a kayıt
 * düşer. Başarı sonrası ilgili list + detail invalidate.
 */
export function useRegenerateEventReportMutation() {
  const qc = useQueryClient();
  return useMutation<EventReportDetailResponse, ApiError, string>({
    mutationFn: (eventId: string) =>
      apiClient<EventReportDetailResponse>(
        `/reports/events/${eventId}/regenerate`,
        { method: "POST" },
      ),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["reports", "events"] });
      qc.setQueryData(eventReportDetailKey(data.eventId), data);
    },
  });
}

// ---------- File exports ----------

/** Backend `File(..., fileName)` başlığından dosya adı; CORS'ta açık (Program.cs). */
export function filenameFromHeader(res: Response): string | null {
  const cd = res.headers.get("content-disposition");
  if (!cd) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * GET /reports/events/{eventId}/export.{xlsx|pdf}?locale= — snapshot'ın dosya hali,
 * arayüz dilinde. Hata ProblemDetails → ApiError (çağıran `showApiError` ile gösterir).
 */
export async function downloadEventReport(
  eventId: string,
  format: "xlsx" | "pdf",
  locale: Locale,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/reports/events/${eventId}/export.${format}?locale=${locale}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    throw problemToApiError(
      response.status,
      await response.json().catch(() => null),
    );
  }
  const blob = await response.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download =
    filenameFromHeader(response) ?? `${locale === "tr" ? "etkinlik-raporu" : "event-report"}.${format}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}
